import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { api } from "../services/api";
import ProductCard from "../components/product/ProductCard";
import { Slider } from "../components/ui/slider";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { formatPrice } from "../utils/formatPrice";

const SORTS = [
  { value: "newest", label: "Nouveautés" },
  { value: "popular", label: "Plus populaire" },
  { value: "price_asc", label: "Prix croissant" },
  { value: "price_desc", label: "Prix décroissant" },
];

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("search") || "";

  const [priceRange, setPriceRange] = useState([0, 50000]);
  const [inStock, setInStock] = useState(false);
  const [sort, setSort] = useState(searchParams.get("sort") || "newest");
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [mobileFilters, setMobileFilters] = useState(false);

  const fetchProducts = useCallback(async (reset = true) => {
    setLoading(true);
    const params = { sort, page: reset ? 1 : page, limit: 12 };
    if (search) params.search = search;
    if (inStock) params.in_stock = true;
    if (priceRange[0] > 0) params.min_price = priceRange[0];
    if (priceRange[1] < 50000) params.max_price = priceRange[1];
    try {
      const { data } = await api.get("/products", { params });
      setTotal(data.total);
      if (reset) setProducts(data.products);
      else setProducts((prev) => [...prev, ...data.products]);
    } finally {
      setLoading(false);
    }
  }, [sort, search, inStock, priceRange, page]);

  useEffect(() => {
    setPage(1);
    fetchProducts(true);
    // eslint-disable-next-line
  }, [sort, search, inStock, priceRange]);

  useEffect(() => {
    if (page > 1) fetchProducts(false);
    // eslint-disable-next-line
  }, [page]);

  const clearFilters = () => {
    setPriceRange([0, 50000]);
    setInStock(false);
    if (search) setSearchParams({});
  };

  const activeChips = [];
  if (inStock) activeChips.push({ label: "En stock", onRemove: () => setInStock(false) });
  if (search) activeChips.push({ label: `« ${search} »`, onRemove: () => setSearchParams({}) });

  const FiltersPanel = () => (
    <div className="space-y-8">
      <div>
        <h3 className="label-eyebrow text-[#111111] mb-4">Prix (DZD)</h3>
        <Slider value={priceRange} onValueChange={setPriceRange} min={0} max={20000} step={200} className="mb-3" data-testid="price-slider" />
        <div className="flex justify-between text-xs text-[#888888] font-body">
          <span>{formatPrice(priceRange[0])}</span>
          <span>{formatPrice(priceRange[1])}</span>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span className="font-body text-sm text-[#111111]">En stock uniquement</span>
        <Switch checked={inStock} onCheckedChange={setInStock} data-testid="filter-instock" />
      </div>

      <button onClick={clearFilters} className="btn-queen-ghost text-xs">Réinitialiser les filtres</button>
    </div>
  );

  return (
    <div className="container-queen pt-32 pb-24" id="main">
      <div className="mb-10">
        <p className="label-eyebrow text-[#C5A880] mb-2">Les Sérums The Queen</p>
        <h1 className="font-display text-[#111111]" style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)" }}>
          Tous les Sérums
        </h1>
      </div>

      <div className="grid lg:grid-cols-[250px_1fr] gap-10">
        <aside className="hidden lg:block">
          <FiltersPanel />
        </aside>

        <div>
          <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
            <p className="font-body text-sm text-[#888888]" data-testid="results-count">{total} soin{total > 1 ? "s" : ""} disponible{total > 1 ? "s" : ""}</p>
            <div className="flex items-center gap-3">
              <button onClick={() => setMobileFilters(true)} className="lg:hidden btn-queen-secondary" style={{ height: 44, padding: "0 18px" }}>
                <SlidersHorizontal size={16} /> Filtres
              </button>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-[190px] rounded-full border-[#EAE8E4]" style={{ height: 44 }} data-testid="sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {activeChips.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {activeChips.map((chip, i) => (
                <button key={i} onClick={chip.onRemove} className="flex items-center gap-1.5 bg-[#F8F8F6] text-[#111111] border border-[#EAE8E4] rounded-full text-xs font-body" style={{ padding: "6px 12px" }}>
                  {chip.label} <X size={13} />
                </button>
              ))}
            </div>
          )}

          {loading && products.length === 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i}><Skeleton className="w-full rounded-2xl aspect-[4/5]" /><Skeleton className="h-4 w-1/2 mt-4" /><Skeleton className="h-5 w-1/3 mt-2" /></div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-24">
              <p className="font-display text-3xl text-[#111111]">Aucun soin trouvé</p>
              <p className="text-[#888888] mt-2 text-sm">Ajustez vos filtres pour voir d'autres rituels.</p>
              <button onClick={clearFilters} className="btn-queen-primary mt-6">Réinitialiser</button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {products.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
              {products.length < total && (
                <div className="text-center mt-12">
                  <button onClick={() => setPage((p) => p + 1)} className="btn-queen-secondary" data-testid="load-more">Charger plus</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile filters bottom sheet */}
      {mobileFilters && (
        <div className="fixed inset-0 z-[80] lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileFilters(false)} />
          <div className="absolute bottom-0 inset-x-0 bg-white rounded-t-3xl p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl">Filtres</h2>
              <button onClick={() => setMobileFilters(false)} aria-label="Fermer" className="grid place-items-center" style={{ width: 44, height: 44 }}><X size={24} /></button>
            </div>
            <FiltersPanel />
            <button onClick={() => setMobileFilters(false)} className="btn-queen-primary w-full mt-8">Voir les résultats ({total})</button>
          </div>
        </div>
      )}
    </div>
  );
}
